// This grammar is deliberately separate from the native report expression AST.
// Neither parsing nor evaluation delegates any operation to template-provided code.
export const LEGACY_EXPRESSION_LIMITS = Object.freeze({
  source: 8192,
  tokens: 2048,
  nodes: 2048,
  depth: 64,
  scan: 100000,
  evaluation: 50000,
  string: 65536,
  array: 10000,
  generatedCharacters: 1048576,
  generatedArrayItems: 50000,
  templateNodes: 50000,
  templateExpressions: 1000,
  templateSource: 262144,
});

export class LegacyExpressionError extends Error {
  constructor(reason = 'UNSUPPORTED_EXPRESSION') {
    super(reason === 'EXPRESSION_LIMIT'
      ? 'Report expression exceeds the supported limits.'
      : 'Report contains an unsupported expression or binding.');
    this.name = 'LegacyExpressionError';
    this.status = 400;
    this.code = 'UNSUPPORTED_LEGACY_EXPRESSION';
    this.reason = reason;
  }
}

const fail = () => { throw new LegacyExpressionError(); };
const limit = () => { throw new LegacyExpressionError('EXPRESSION_LIMIT'); };
const redacted = action => {
  try { return action(); } catch (error) {
    if (error instanceof LegacyExpressionError) throw error;
    throw new LegacyExpressionError();
  }
};
const forbidden = new Set([
  '__proto__', 'prototype', 'constructor', '__defineGetter__', '__defineSetter__',
  '__lookupGetter__', '__lookupSetter__', 'toString', 'valueOf', 'toLocaleString',
  'global', 'globalThis', 'window', 'self', 'parent', 'top', 'frames', 'document',
  'location', 'navigator', 'process', 'require', 'module', 'exports', 'import',
  'Function', 'eval', 'Object', 'Array', 'Reflect', 'Proxy', 'Symbol', 'Promise',
  'Math', 'JSON', 'Date', 'Intl', 'RegExp', 'WebAssembly', 'fetch', 'XMLHttpRequest',
  'Worker', 'SharedWorker', 'setTimeout', 'setInterval', 'queueMicrotask',
  'this', 'new', 'function', 'class', 'async', 'await', 'yield', 'delete',
  'typeof', 'void', 'instanceof', 'in', 'let', 'const', 'var', 'return', 'throw',
  'try', 'catch', 'if', 'else', 'for', 'while', 'do', 'switch', 'with', 'debugger',
]);
const helpers = new Set(['sum', 'money', 'date', 'Number', 'String', 'Boolean']);
const methods = new Set(['map', 'reduce', 'toFixed']);
const identifier = /^[A-Za-z_$][A-Za-z0-9_$]*$/;
const indexKey = /^(0|[1-9]\d*)$/;
const safeKey = key => {
  if (typeof key !== 'string' || forbidden.has(key) ||
      !(identifier.test(key) || indexKey.test(key))) fail();
  return key;
};
const precedence = {
  '??': 1, '||': 2, '&&': 3,
  '==': 4, '!=': 4, '===': 4, '!==': 4,
  '<': 5, '<=': 5, '>': 5, '>=': 5,
  '+': 6, '-': 6, '*': 7, '/': 7, '%': 7,
};

function stringValue(value) {
  if (value.length > LEGACY_EXPRESSION_LIMITS.string) limit();
  return value;
}

function primitive(value) {
  if (value !== null && !['undefined', 'boolean', 'number', 'string'].includes(typeof value)) fail();
  if (typeof value === 'string') stringValue(value);
  return value;
}

function checked(value) {
  if (typeof value === 'function' || typeof value === 'symbol' || typeof value === 'bigint') fail();
  if (typeof value === 'string') stringValue(value);
  if (Array.isArray(value) && value.length > LEGACY_EXPRESSION_LIMITS.array) limit();
  return value;
}

function own(object, key) {
  safeKey(key);
  if (object == null) return undefined;
  checked(object);
  if (typeof object === 'string') {
    if (key === 'length') return object.length;
    return indexKey.test(key) ? object[Number(key)] : undefined;
  }
  if (typeof object !== 'object') return undefined;
  const descriptor = Object.getOwnPropertyDescriptor(object, key);
  if (!descriptor) return undefined;
  if (!Object.prototype.hasOwnProperty.call(descriptor, 'value')) fail();
  return checked(descriptor.value);
}

export function getLegacyPath(path, data) {
  return redacted(() => {
    if (typeof path !== 'string') fail();
    if (path.length > LEGACY_EXPRESSION_LIMITS.source) limit();
    const keys = path.split('.');
    if (keys.length > LEGACY_EXPRESSION_LIMITS.depth) limit();
    keys.forEach(safeKey);
    return keys.reduce((value, key) => own(value, key), data);
  });
}

function parse(source) {
  if (typeof source !== 'string' || !source.trim()) fail();
  if (source.length > LEGACY_EXPRESSION_LIMITS.source) limit();
  const budget = { tokens: 0, nodes: 0, scan: 0 };
  const scan = () => { if (++budget.scan > LEGACY_EXPRESSION_LIMITS.scan) limit(); };
  const node = (kind, fields) => {
    if (++budget.nodes > LEGACY_EXPRESSION_LIMITS.nodes) limit();
    return { kind, ...fields };
  };
  const depthCheck = depth => { if (depth > LEGACY_EXPRESSION_LIMITS.depth) limit(); };

  class Lexer {
    constructor(text) { this.text = text; this.pos = 0; }
    take() { scan(); return this.text[this.pos++]; }
    escape() {
      const char = this.take();
      const escapes = { n: '\n', r: '\r', t: '\t', b: '\b', f: '\f', v: '\v', '0': '\0' };
      if (Object.prototype.hasOwnProperty.call(escapes, char)) return escapes[char];
      if (['\\', "'", '"', '`', '$'].includes(char)) return char;
      if (char === 'u' || char === 'x') {
        const count = char === 'u' ? 4 : 2;
        const hex = this.text.slice(this.pos, this.pos + count);
        if (!new RegExp(`^[0-9a-fA-F]{${count}}$`).test(hex)) fail();
        this.pos += count;
        return String.fromCharCode(parseInt(hex, 16));
      }
      fail();
    }
    quoted(quote) {
      let value = '';
      while (this.pos < this.text.length) {
        const char = this.take();
        if (char === quote) return value;
        if (char === '\n' || char === '\r') fail();
        value += char === '\\' ? this.escape() : char;
      }
      fail();
    }
    interpolation(depth) {
      depthCheck(depth);
      const start = this.pos;
      let braces = 1;
      while (this.pos < this.text.length) {
        const char = this.take();
        if (char === '"' || char === "'") this.quoted(char);
        else if (char === '`') this.template(depth + 1);
        else if (char === '{') { braces++; depthCheck(depth + braces); }
        else if (char === '}' && --braces === 0) return this.text.slice(start, this.pos - 1);
      }
      fail();
    }
    template(depth) {
      depthCheck(depth);
      const parts = [];
      let text = '';
      while (this.pos < this.text.length) {
        const char = this.take();
        if (char === '`') { parts.push(text); return parts; }
        if (char === '\\') text += this.escape();
        else if (char === '$' && this.text[this.pos] === '{') {
          this.take();
          parts.push(text, { source: this.interpolation(depth + 1) });
          text = '';
        } else text += char;
      }
      fail();
    }
    next() {
      while (/\s/.test(this.text[this.pos] || '') && this.pos < this.text.length) this.take();
      if (++budget.tokens > LEGACY_EXPRESSION_LIMITS.tokens) limit();
      if (this.pos === this.text.length) return { type: 'end' };
      const char = this.take();
      if (char === '"' || char === "'") return { type: 'literal', value: this.quoted(char) };
      if (char === '`') return { type: 'template', value: this.template(1) };
      if (/[0-9]/.test(char) || (char === '.' && /[0-9]/.test(this.text[this.pos] || ''))) {
        const match = /^(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/.exec(this.text.slice(this.pos - 1));
        this.pos += match[0].length - 1;
        const value = Number(match[0]);
        if (!Number.isFinite(value)) fail();
        return { type: 'literal', value };
      }
      if (/[A-Za-z_$]/.test(char)) {
        const match = /^[A-Za-z_$][A-Za-z0-9_$]*/.exec(this.text.slice(this.pos - 1))[0];
        this.pos += match.length - 1;
        if (forbidden.has(match)) fail();
        if (match === 'true' || match === 'false' || match === 'null' || match === 'undefined') {
          return { type: 'literal', value: { true: true, false: false, null: null, undefined }[match] };
        }
        return { type: 'id', value: match };
      }
      for (const operator of ['===', '!==', '=>', '?.', '??', '||', '&&', '==', '!=', '<=', '>=']) {
        if (this.text.startsWith(operator, this.pos - 1)) {
          this.pos += operator.length - 1;
          return { type: operator };
        }
      }
      if ('+-*/%!?():.,[]<>'.includes(char)) return { type: char };
      fail();
    }
  }

  class Parser {
    constructor(text, initialDepth) {
      this.lexer = new Lexer(text);
      this.current = this.lexer.next();
      this.initialDepth = initialDepth;
    }
    advance() { const token = this.current; this.current = this.lexer.next(); return token; }
    accept(type) { if (this.current.type !== type) return false; this.advance(); return true; }
    expect(type) { if (!this.accept(type)) fail(); }
    expression(min = 0, depth = this.initialDepth) {
      depthCheck(depth);
      let left = this.prefix(depth + 1);
      while (Object.prototype.hasOwnProperty.call(precedence, this.current.type) &&
             precedence[this.current.type] >= min) {
        const operator = this.advance().type;
        left = node('binary', { operator, left, right: this.expression(precedence[operator] + 1, depth + 1) });
      }
      if (min === 0 && this.accept('?')) {
        const yes = this.expression(0, depth + 1);
        this.expect(':');
        left = node('conditional', { condition: left, yes, no: this.expression(0, depth + 1) });
      }
      return left;
    }
    prefix(depth) {
      depthCheck(depth);
      let result;
      if (['!', '+', '-'].includes(this.current.type)) {
        return node('unary', { operator: this.advance().type, value: this.expression(8, depth + 1) });
      }
      if (this.current.type === 'literal') result = node('literal', { value: this.advance().value });
      else if (this.current.type === 'template') {
        const parts = this.advance().value.map(part => typeof part === 'string' ? part : parsePart(part.source, depth + 1));
        result = node('template', { parts });
      } else if (this.current.type === 'id') result = node('id', { name: this.advance().value });
      else if (this.accept('(')) {
        result = this.expression(0, depth + 1);
        this.expect(')');
      } else if (this.accept('[')) {
        const values = [];
        if (!this.accept(']')) {
          do { values.push(this.expression(0, depth + 1)); } while (this.accept(','));
          this.expect(']');
        }
        result = node('array', { values });
      } else fail();

      while (true) {
        let key;
        if (this.current.type === '.' || this.current.type === '?.') {
          const optional = this.advance().type === '?.';
          if (this.current.type === '[') key = this.computedKey();
          else {
            if (this.current.type !== 'id') fail();
            key = safeKey(this.advance().value);
          }
          result = node('member', { object: result, key, optional });
        } else if (this.current.type === '[') {
          result = node('member', { object: result, key: this.computedKey() });
        } else if (this.accept('(')) {
          const args = [];
          if (result.kind === 'member' && ['map', 'reduce'].includes(result.key)) {
            args.push(this.callback(depth + 1));
            if (this.accept(',')) args.push(this.expression(0, depth + 1));
            this.expect(')');
            if (result.key === 'map' && args.length !== 1) fail();
          } else {
            if (!this.accept(')')) {
              do { args.push(this.expression(0, depth + 1)); } while (this.accept(','));
              this.expect(')');
            }
          }
          if (result.kind === 'id' && helpers.has(result.name)) {
            if (args.length > (result.name === 'money' ? 2 : 1)) fail();
            result = node('helper', { name: result.name, args });
          } else if (result.kind === 'member' && methods.has(result.key)) {
            if (result.key === 'toFixed' && args.length > 1) fail();
            result = node('method', { object: result.object, name: result.key, optional: result.optional, args });
          } else fail();
        } else break;
      }
      return result;
    }
    computedKey() {
      this.expect('[');
      if (this.current.type !== 'literal') fail();
      const value = this.advance().value;
      if (typeof value !== 'string' &&
          !(typeof value === 'number' && Number.isSafeInteger(value) && value >= 0)) fail();
      this.expect(']');
      return safeKey(String(value));
    }
    callback(depth) {
      depthCheck(depth);
      const params = [];
      if (this.accept('(')) {
        do {
          if (this.current.type !== 'id') fail();
          params.push(this.advance().value);
        } while (this.accept(','));
        this.expect(')');
      } else {
        if (this.current.type !== 'id') fail();
        params.push(this.advance().value);
      }
      if (params.length > 3 || new Set(params).size !== params.length ||
          params.some(name => helpers.has(name) || name === 'data')) fail();
      this.expect('=>');
      return node('callback', { params, body: this.expression(0, depth + 1) });
    }
  }
  function parsePart(text, depth) {
    depthCheck(depth);
    const parser = new Parser(text, depth);
    const ast = parser.expression();
    if (parser.current.type !== 'end') fail();
    return ast;
  }
  const ast = parsePart(source, 0);
  // Postfix chains and left-associative operators also contribute to tree depth.
  const pending = [[ast, 0]];
  while (pending.length) {
    const [current, depth] = pending.pop();
    depthCheck(depth);
    for (const value of Object.values(current)) {
      if (value && typeof value === 'object') {
        if (Array.isArray(value)) {
          value.forEach(child => { if (child && typeof child === 'object') pending.push([child, depth + 1]); });
        } else pending.push([value, depth + 1]);
      }
    }
  }
  return ast;
}

export function validateLegacyExpression(source) {
  parse(source);
  return true;
}

export function validateLegacyTemplate(template) {
  return redacted(() => validateTemplate(template));
}

function validateTemplate(template) {
  const pending = [[template, 0]];
  let nodes = 0;
  let expressions = 0;
  let sourceSize = 0;
  while (pending.length) {
    const [value, depth] = pending.pop();
    if (++nodes > LEGACY_EXPRESSION_LIMITS.templateNodes || depth > LEGACY_EXPRESSION_LIMITS.depth) limit();
    if (!value || typeof value !== 'object') continue;
    const keys = Object.keys(value);
    if (keys.length > LEGACY_EXPRESSION_LIMITS.templateNodes - nodes - pending.length) limit();
    for (const key of keys) {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, 'value')) fail();
      const child = descriptor.value;
      if (key === 'expr' && typeof child === 'string') {
        if (++expressions > LEGACY_EXPRESSION_LIMITS.templateExpressions) limit();
        sourceSize += child.length;
        if (sourceSize > LEGACY_EXPRESSION_LIMITS.templateSource) limit();
        validateLegacyExpression(child);
      }
      // Native expressions are objects, not legacy JavaScript strings. Their own
      // admission validator remains responsible for their operators and types.
      if (key === 'path' && typeof child === 'string') getLegacyPath(child, undefined);
      pending.push([child, depth + 1]);
    }
  }
  return true;
}

export function evaluateLegacyExpression(source, data) {
  const ast = parse(source);
  const missingValue = Symbol();
  const optionalSkip = Symbol();
  let remaining = LEGACY_EXPRESSION_LIMITS.evaluation;
  let characters = 0;
  let arrayItems = 0;
  const tick = () => { if (--remaining < 0) limit(); };
  const output = value => {
    checked(value);
    if (typeof value === 'string') {
      characters += value.length;
      if (characters > LEGACY_EXPRESSION_LIMITS.generatedCharacters) limit();
    }
    return value;
  };
  const allocateArray = size => {
    arrayItems += size;
    if (arrayItems > LEGACY_EXPRESSION_LIMITS.generatedArrayItems) limit();
  };
  const asString = value => output(String(primitive(value)));
  const asNumber = value => Number(primitive(value));
  const resolved = (current, scope, depth) => {
    const value = evaluate(current, scope, depth);
    return value === optionalSkip ? undefined : value;
  };
  const argsOf = (args, scope, depth) => args.map(arg => resolved(arg, scope, depth + 1));
  const formatDate = value => {
    primitive(value);
    if (!value) return '';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString();
    }
    return new Date(value).toLocaleDateString();
  };
  function evaluate(current, scope, depth = 0) {
    tick();
    if (depth > LEGACY_EXPRESSION_LIMITS.depth) limit();
    switch (current.kind) {
      case 'literal': return current.value;
      case 'id': {
        if (current.name === 'data') return checked(data);
        for (let i = scope.length - 1; i >= 0; i--) {
          if (scope[i].has(current.name)) return checked(scope[i].get(current.name));
        }
        if (data == null || !Object.prototype.hasOwnProperty.call(data, current.name)) throw missingValue;
        return own(data, current.name);
      }
      case 'member': {
        const object = evaluate(current.object, scope, depth + 1);
        if (object === optionalSkip) return optionalSkip;
        if (object == null) {
          if (current.optional) return optionalSkip;
          throw missingValue;
        }
        return own(object, current.key);
      }
      case 'array':
        allocateArray(current.values.length);
        return checked(argsOf(current.values, scope, depth));
      case 'template': {
        let text = '';
        for (const part of current.parts) {
          tick();
          text = output(text + (typeof part === 'string' ? part : asString(resolved(part, scope, depth + 1))));
        }
        return text;
      }
      case 'unary': {
        const value = primitive(resolved(current.value, scope, depth + 1));
        if (current.operator === '!') return !value;
        return current.operator === '-' ? -asNumber(value) : asNumber(value);
      }
      case 'conditional': return resolved(
        resolved(current.condition, scope, depth + 1) ? current.yes : current.no, scope, depth + 1);
      case 'binary': {
        const left = resolved(current.left, scope, depth + 1);
        if (current.operator === '&&') return left && resolved(current.right, scope, depth + 1);
        if (current.operator === '||') return left || resolved(current.right, scope, depth + 1);
        if (current.operator === '??') return left ?? resolved(current.right, scope, depth + 1);
        const right = resolved(current.right, scope, depth + 1);
        primitive(left); primitive(right);
        switch (current.operator) {
          case '+': return output(left + right);
          case '-': return left - right;
          case '*': return left * right;
          case '/': return left / right;
          case '%': return left % right;
          case '<': return left < right;
          case '<=': return left <= right;
          case '>': return left > right;
          case '>=': return left >= right;
          // Loose equality is safe here because both operands are primitives.
          // eslint-disable-next-line eqeqeq
          case '==': return left == right;
          // eslint-disable-next-line eqeqeq
          case '!=': return left != right;
          case '===': return left === right;
          case '!==': return left !== right;
          default: return fail();
        }
      }
      case 'helper': {
        const args = argsOf(current.args, scope, depth);
        const value = args[0];
        switch (current.name) {
          case 'Number': return args.length ? asNumber(value) : 0;
          case 'String': return args.length ? asString(value) : '';
          case 'Boolean': return Boolean(value);
          case 'date': return output(formatDate(value));
          case 'money': {
            const currency = args.length > 1 ? primitive(args[1]) : 'USD';
            if (typeof currency !== 'string' || !/^[A-Za-z]{3}$/.test(currency)) fail();
            return output(new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(asNumber(value || 0)));
          }
          case 'sum': {
            if (!Array.isArray(value)) return 0;
            checked(value);
            let total = 0;
            for (let i = 0; i < value.length; i++) {
              tick();
              const item = own(value, String(i));
              if (typeof item === 'number') total += item;
            }
            return total;
          }
          default: return fail();
        }
      }
      case 'method': {
        const value = evaluate(current.object, scope, depth + 1);
        if (value === optionalSkip) return optionalSkip;
        if (value == null) {
          if (current.optional) return optionalSkip;
          throw missingValue;
        }
        if (current.name === 'toFixed') {
          if (typeof value !== 'number') fail();
          const digits = current.args.length ? resolved(current.args[0], scope, depth + 1) : 0;
          if (!Number.isInteger(digits) || digits < 0 || digits > 20) fail();
          return output(value.toFixed(digits));
        }
        if (!Array.isArray(value)) fail();
        checked(value);
        if (current.name === 'map') allocateArray(value.length);
        const callback = current.args[0];
        let result = current.name === 'map' ? [] :
          current.args.length > 1 ? resolved(current.args[1], scope, depth + 1) : undefined;
        let hasInitial = current.args.length > 1;
        for (let i = 0; i < value.length; i++) {
          tick();
          if (!Object.prototype.hasOwnProperty.call(value, String(i))) {
            if (current.name === 'map') result.length++;
            continue;
          }
          const item = own(value, String(i));
          if (current.name === 'reduce' && !hasInitial) {
            result = item; hasInitial = true; continue;
          }
          const values = current.name === 'map' ? [item, i, value] : [result, item, i];
          const locals = new Map(callback.params.map((name, index) => [name, values[index]]));
          const output = resolved(callback.body, [...scope, locals], depth + 1);
          if (current.name === 'map') result.push(output);
          else result = output;
        }
        return checked(result);
      }
      default: return fail();
    }
  }
  return redacted(() => {
    try { return resolved(ast, [], 0); } catch (error) {
      if (error === missingValue) return undefined;
      throw error;
    }
  });
}
